import { BitkitAlert, BitkitAlertProps, BitkitLink } from '@bitrise/bitkit-v2';
import { ReactElement } from 'react';

import useBuildDetailsPageStore from '@/pages/BuildDetailsPage/BuildDetailsPage.store';
import { appSettingsPath, billingPath, workflowEditorPath } from '@/utils/monolithApiRouteService';

type BannersData = {
  content: string | ReactElement;
  variant: BitkitAlertProps['variant'];
};

export type BuildAlertsProps = {
  hasPaymentProblems: boolean;
  isOwner: boolean;
  isAccountPrivateOrDedicatedCloud?: boolean;
  stackDeprecationDate?: string;
};

const BuildAlerts = ({
  hasPaymentProblems,
  isOwner,
  isAccountPrivateOrDedicatedCloud,
  stackDeprecationDate,
}: BuildAlertsProps) => {
  const banners: BannersData[] = [];

  const app = useBuildDetailsPageStore((state) => state.app);
  const build = useBuildDetailsPageStore((state) => state.build);
  const abilities = useBuildDetailsPageStore((state) => state.abilities);

  if (app.isDisabled) {
    banners.push({
      content: (
        <>
          This project has been disabled. You can re-enable it on your{' '}
          {abilities.canChangeSettings ? (
            <>
              <BitkitLink href={appSettingsPath(app.slug, 'builds')} textDecoration="underline">
                Settings page
              </BitkitLink>{' '}
              to start building again.
            </>
          ) : (
            <>Settings page to start building again.</>
          )}
        </>
      ),
      variant: 'critical',
    });
  }

  if (hasPaymentProblems) {
    const content = isOwner ? (
      <>
        Payment for your subscription is now overdue. To ensure continued access to Bitrise, please review your{' '}
        <BitkitLink href={billingPath(app.ownerType || 'User')} textDecoration="underline">
          billing information.
        </BitkitLink>
      </>
    ) : (
      <>
        Payment for this {app.ownerType === 'Organization' ? ' workspace ' : " projects's workspace "} is now overdue.
        To ensure continued access to Bitrise, please contact <strong>{app.owner?.username}</strong> to review their
        billing information.
      </>
    );
    banners.push({
      content,
      variant: 'critical',
    });
  }

  if (stackDeprecationDate && isAccountPrivateOrDedicatedCloud === false) {
    banners.push({
      content: (
        <>
          This workflow is using a deprecated stack.{' '}
          {abilities.canChangeSettings ? (
            <>
              <BitkitLink href={workflowEditorPath(app.slug, build.triggeredWorkflow)} textDecoration="underline">
                Change your build stack
              </BitkitLink>
              , or your builds will not work after {stackDeprecationDate}
            </>
          ) : (
            <>Change your build stack, or your builds will not work after {stackDeprecationDate}</>
          )}
        </>
      ),
      variant: 'warning',
    });
  }

  if (banners.length === 0) {
    return null;
  }

  return (
    <>
      {banners.map(({ content, variant }, index) => (
        //  eslint-disable-next-line react/no-array-index-key
        <BitkitAlert key={index} variant={variant} marginBlockEnd="24" messageText={content} />
      ))}
    </>
  );
};

export default BuildAlerts;
